import classNames from 'classnames';
import { stringify } from 'qs';
import { useMemo } from 'react';
import { Button, Heading } from 'yoso';
import { BaseCard } from '~/components/Card';
import { T } from '~/components/I18n/Translation';
import { Chevron } from '~/components/Icon/v2/Chevron';
import { useLandEvent } from '~/components/Land/Event';
import { useNewWinningLandHistory } from '~/components/Land/History/LandHistory.hooks';
import { LandStatusBarDescriptionList } from '~/components/Land/StatusBar/DescriptionList/LandStatusBarDescriptionList';
import { useHasNewLandHistory } from '~/components/Land/StatusBar/LandStatusBar.hook';
import { Link, LinkProps } from '~/components/Route/Link';
import { StatusBarAccordion } from '~/components/Status/Bar/Accordion/StatusBarAccordion';
import { StatusBarProfile } from '~/components/Status/Bar/Profile/StatusBarProfile';
import statusBarStyles from '~/components/Status/Bar/StatusBar.module.scss';
import myItemsStyles from '~/components/Status/Bar/StatusBarMyItems.module.scss';
import styles from './LandStatusBar.module.scss';

export type LandStatusBarProps = {
  className?: string;
  id: number;
};

export const LandStatusBar = ({ className, id }: LandStatusBarProps) => {
  const {
    data: { landList, status },
  } = useLandEvent(id);
  const participationCount = useMemo(
    () => landList.filter((land) => !!land?.myParticipation).length,
    [landList]
  );

  const showReddot = useHasNewLandHistory();
  const { value: uncheckedWinningLand } = useNewWinningLandHistory();

  return (
    <div className={classNames(styles.root, statusBarStyles.root, className)}>
      <StatusBarProfile className={statusBarStyles.header}>
        <Link
          to={`/land/event/history${stringify(
            {
              category: status === 'IN_PROGRESS' ? 'ONGOING' : 'ALL',
            },
            { addQueryPrefix: true }
          )}`}
          className={styles.description}>
          <T name="citizen_land_menu_myhistory_latest_others" options={[id, participationCount]} />
          <Chevron className={styles.chevron} direction="right" />
        </Link>
      </StatusBarProfile>
      <div className={statusBarStyles.body}>
        <StatusBarAccordion>
          <Heading level={5} className={myItemsStyles.title}>
            <T name="citizen_land_menu_myhistory_asset_desc" />
          </Heading>
          <LandStatusBarDescriptionList />
        </StatusBarAccordion>
      </div>
      <div className={statusBarStyles.aside}>
        <Button<LinkProps>
          as={Link}
          to="/land/event/history"
          className={statusBarStyles.button}
          size="xsmall"
          data-event="click"
          data-event-target="land_myhistory_btn">
          <T name="citizen_land_myhistory_title" />
          {showReddot && <span className={styles.reddot} />}
        </Button>
      </div>
      {!!uncheckedWinningLand && (
        <div className={statusBarStyles.footer}>
          <BaseCard className={styles.winner}>
            <T name="citizen_land_menu_myhistory_win_desc" options={[uncheckedWinningLand.week]} />{' '}
            <Link to="/land/event/history?category=WINS" className={styles.winnerLink}>
              <T name="citizen_button_title_seedetails" />
            </Link>
          </BaseCard>
        </div>
      )}
    </div>
  );
};
